/**
 * MobileStreamingHUD — full-screen streaming overlay for Primate Vision V2 on mobile.
 *
 * When a user submits a prompt and a live stream starts:
 *  - The whole screen becomes the annotated (server-rendered) video stream
 *  - A top bar shows connection status + stop button
 *  - A bottom HUD strip shows prompt update field + live detections
 *
 * Design spec: see task "feat(mobile): full-screen streaming HUD overlay"
 */

import { useRef, useEffect, useState } from 'react';
import type { ConnectionState, StreamMetrics, StreamQueueState } from '../../hooks/types';
import type { NarrativeEntry } from '../../lib/narrative';
import { useThemeColors } from '../../hooks/useThemeColors';

// ─── Static colours (not theme-dependent) ────────────────────────────────────
// Theme-dependent colours are accessed via useThemeColors() inside the component.
const STATIC = {
  stopRed:     '#DC2626',
  detectGreen: '#16A34A',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function ConnectionDot({ state }: { state: ConnectionState }) {
  const c = useThemeColors();
  const isLive       = state === 'live' || state === 'degraded' || state === 'quota_warning';
  const isBuffering  = state === 'buffering';
  const isConnecting = state === 'connecting';
  const isStopping   = state === 'stopping';

  const color = isLive
    ? STATIC.detectGreen
    : isStopping
    ? c.textMuted
    : c.signal;

  return (
    <span style={{
      display: 'inline-block',
      width: 8,
      height: 8,
      borderRadius: '50%',
      background: color,
      animation: (isConnecting || isBuffering) ? 'hud-pulse 1.2s ease-in-out infinite' : 'none',
      flexShrink: 0,
    }} />
  );
}

function statusLabel(state: ConnectionState): string {
  switch (state) {
    case 'connecting':       return 'CONNECTING';
    case 'buffering':        return 'BUFFERING';
    case 'live':             return 'LIVE';
    case 'degraded':         return 'LIVE';
    case 'quota_warning':    return 'LIVE';
    case 'recalculating':    return 'LIVE';
    case 'stopping':         return 'STOPPING...';
    default:                 return 'CONNECTING';
  }
}

// ─── Props ────────────────────────────────────────────────────────────────────

export interface MobileStreamingHUDProps {
  // Stream data
  remoteStream:      MediaStream | null;
  connectionState:   ConnectionState;
  samEnabled?:       boolean;
  isFileStream?:     boolean;
  metrics:           StreamMetrics;

  // Prompt
  activePrompt:      string;
  onPromptUpdate:    (prompt: string) => void;
  promptUpdateError: string | null;
  isRecalculating:   boolean;

  // Actions
  onStop:            () => void;

  // Status
  queue:             StreamQueueState | null;
  stopReason:        string | null;

  // Narrative entries from the current stream session
  narrative:         NarrativeEntry[];

  // Local webcam stream — shown as placeholder while remote stream is connecting
  localStream:       MediaStream | null;
  // True when the active camera is front-facing (selfie). Preview is mirrored to
  // match native iOS camera behaviour; back camera is never mirrored.
  isFrontCamera?:    boolean;
  // Called when user taps the minimize/collapse button — returns to the result pill view
  onMinimize?:       () => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function MobileStreamingHUD({
  remoteStream,
  connectionState,
  metrics,
  activePrompt,
  onPromptUpdate,
  promptUpdateError,
  isRecalculating,
  onStop,
  queue,
  // stopReason available for future use (e.g. displaying a reason after stream ends)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  stopReason: _stopReason,
  narrative,
  localStream,
  isFrontCamera = false,
  onMinimize,
  samEnabled = false,
  isFileStream = false,
}: MobileStreamingHUDProps) {
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [localPrompt, setLocalPrompt] = useState(activePrompt);
  const c = useThemeColors(); // replaces hardcoded const C block (Phase 1, PRI-291)

  // Sync when activePrompt changes from outside (e.g. recalculating done)
  useEffect(() => {
    setLocalPrompt(activePrompt);
  }, [activePrompt]);

  // Screen wake lock — prevents browser from throttling/dimming during live streaming,
  // which causes encoding quality degradation on mobile.
  useEffect(() => {
    let wakeLock: WakeLockSentinel | null = null;
    const acquire = async () => {
      try {
        if ('wakeLock' in navigator) {
          wakeLock = await navigator.wakeLock.request('screen');
          // Null the ref when the OS releases the lock (e.g. on page hide),
          // so handleVisibilityChange can re-acquire on the next foreground.
          wakeLock.addEventListener('release', () => { wakeLock = null; });
        }
      } catch {
        // Wake lock denied (battery saver etc.) — non-fatal
      }
    };
    void acquire();
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && wakeLock === null) void acquire();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      wakeLock?.release().catch(() => {});
    };
  }, []);

  // Attach local stream to the placeholder <video> element
  useEffect(() => {
    const el = localVideoRef.current;
    if (!el) return;
    if (localStream) {
      el.srcObject = localStream;
    } else {
      el.srcObject = null;
    }
  }, [localStream]);

  // Attach remote stream to the <video> element
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    if (remoteStream) {
      el.srcObject = remoteStream;
    } else {
      el.srcObject = null;
    }
  }, [remoteStream]);

  const isLiveState = ['live', 'degraded', 'quota_warning', 'recalculating', 'stopping'].includes(connectionState);
  // Always show local camera as the base layer — gives instant visual feedback regardless of SAM.
  // Remote stream fades in on top once it's live (250ms transition).
  const showRemote = !!(remoteStream && isLiveState);
  const promptChanged = localPrompt.trim() !== '' && localPrompt !== activePrompt;

  // Prefer narrative entries (human-readable); fall back to raw detections
  const latestNarrative = narrative
    .filter(n => n.type !== 'prompt_change' && n.type !== 'status')
    .slice(0, 3);
  const detections = metrics.detections.slice(0, 4);
  const hasNarrative = latestNarrative.length > 0;

  function handlePromptUpdate() {
    if (!localPrompt.trim() || isRecalculating) return;
    onPromptUpdate(localPrompt.trim());
  }

  return (
    <>
      {/* Keyframe animations injected inline */}
      <style>{`
        @keyframes hud-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
        @keyframes hud-spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        @keyframes hud-fade-in {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        .hud-stop-btn:active { opacity: 0.7; }
        .hud-update-btn:active { opacity: 0.7; }
        .hud-textarea::placeholder { color: ${c.textMuted}; }
        .hud-textarea:focus { outline: none; border-color: ${c.signal}; }
      `}</style>

      {/* ── Full-screen fixed overlay ── */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 150,
          background: c.bgPrimary,
          display: 'flex',
          flexDirection: 'column',
          animation: 'hud-fade-in 200ms ease both',
        }}
      >
        {/* ── Top bar ── */}
        <div style={{
          position: 'relative',
          height: 48,
          flexShrink: 0,
          background: 'rgba(9, 9, 13, 0.85)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 12px',
          gap: 8,
          paddingTop: 'env(safe-area-inset-top, 0px)',
        }}>
          {/* Left: minimize chevron + dot + status */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {onMinimize && (
              <button
                onClick={onMinimize}
                aria-label="Minimize"
                style={{
                  width: 32, height: 32, borderRadius: 8,
                  background: 'transparent', border: 'none',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', marginRight: 2,
                  WebkitTapHighlightColor: 'transparent',
                  color: c.textMuted,
                }}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M3 5l5 5 5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            )}
            <ConnectionDot state={connectionState} />
            <span style={{
              fontFamily: '"JetBrains Mono", "Fira Mono", monospace',
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: '0.08em',
              color: c.textPrimary,
            }}>
              {statusLabel(connectionState)}
            </span>
            {queue && (
              <span style={{
                fontFamily: '"JetBrains Mono", "Fira Mono", monospace',
                fontSize: 10,
                color: c.textMuted,
                marginLeft: 4,
              }}>
                #{queue.position} in queue
              </span>
            )}
          </div>

          {/* Right: Stop button */}
          <button
            className="hud-stop-btn"
            onClick={onStop}
            style={{
              minWidth: 44,
              minHeight: 44,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 4,
              background: 'transparent',
              border: `1.5px solid ${STATIC.stopRed}`,
              borderRadius: 8,
              color: c.textPrimary,
              cursor: 'pointer',
              padding: '0 10px',
              fontFamily: '"JetBrains Mono", "Fira Mono", monospace',
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: '0.04em',
            }}
          >
            <span style={{ fontSize: 10 }}>■</span>
            Stop
          </button>
        </div>

        {/* ── Video area ── */}
        <div style={{
          flex: 1,
          position: 'relative',
          overflow: 'hidden',
          background: c.bgPrimary,
        }}>
          {/* Base layer: local camera — always visible while stream connects.
              Gives immediate visual feedback regardless of SAM or non-SAM prompts.
              Mirror only for front camera; back camera and file streams are never mirrored. */}
          <video
            ref={localVideoRef}
            data-testid="mobile-video-local"
            autoPlay
            playsInline
            muted
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: isFileStream ? 'contain' : 'cover',
              transform: isFrontCamera ? 'scaleX(-1)' : 'none',
              display: localStream ? 'block' : 'none',
            }}
          />

          {/* Top layer: annotated remote stream — fades in when live.
              NO mirror flip — server-rendered output is geometrically correct. */}
          <video
            ref={videoRef}
            data-testid="mobile-video-remote"
            autoPlay
            playsInline
            muted
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: isFileStream ? 'contain' : 'cover',
              opacity: showRemote ? 1 : 0,
              transition: 'opacity 250ms ease',
            }}
          />

          {/* Connecting overlay — shown over both layers while not yet live */}
          {!isLiveState && (
            <div style={{
              position: 'absolute', inset: 0, zIndex: 2,
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center', gap: 12,
              background: localStream ? 'rgba(0,0,0,0.25)' : c.bgPrimary,
              pointerEvents: 'none',
            }}>
              <span style={{
                display: 'inline-block', width: 12, height: 12,
                borderRadius: '50%', background: c.signal,
                animation: 'hud-pulse 1.2s ease-in-out infinite',
              }} />
              <span style={{
                fontFamily: '"JetBrains Mono", "Fira Mono", monospace',
                fontSize: 13, color: c.textMuted,
              }}>
                Connecting...
              </span>
            </div>
          )}
        </div>

        {/* ── Bottom HUD strip ── */}
        <div style={{
          flexShrink: 0,
          background: 'rgba(17, 19, 24, 0.95)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 8px)',
          paddingTop: 8,
        }}>
          {/* Section 1: Prompt input row */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '0 12px 8px',
          }}>
            <textarea
              className="hud-textarea"
              rows={1}
              value={localPrompt}
              onChange={e => setLocalPrompt(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handlePromptUpdate();
                }
              }}
              placeholder="Update what Darwin looks for..."
              style={{
                flex: 1,
                background: 'rgba(255,255,255,0.07)',
                border: '1px solid rgba(255,255,255,0.12)',
                borderRadius: 8,
                color: c.textPrimary,
                fontSize: 14,
                fontFamily: 'inherit',
                padding: '8px 10px',
                resize: 'none',
                lineHeight: '1.4',
                transition: 'border-color 150ms',
              }}
            />
            <button
              className="hud-update-btn"
              onClick={handlePromptUpdate}
              disabled={!promptChanged || isRecalculating}
              style={{
                minWidth: 44,
                minHeight: 44,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 4,
                background: promptChanged && !isRecalculating ? c.signal : 'rgba(255,255,255,0.08)',
                border: 'none',
                borderRadius: 8,
                color: c.textPrimary,
                cursor: promptChanged && !isRecalculating ? 'pointer' : 'default',
                opacity: !promptChanged && !isRecalculating ? 0.5 : 1,
                padding: '0 10px',
                fontSize: 13,
                fontFamily: '"JetBrains Mono", "Fira Mono", monospace',
                fontWeight: 600,
                flexShrink: 0,
                transition: 'background 150ms, opacity 150ms',
                whiteSpace: 'nowrap',
              }}
            >
              {isRecalculating ? (
                <>
                  <span style={{ display: 'inline-block', animation: 'hud-spin 1s linear infinite', fontSize: 12 }}>↻</span>
                  <span style={{ fontSize: 11 }}>Recalculating…</span>
                </>
              ) : (
                <>
                  <span style={{ fontSize: 12 }}>↻</span>
                  Update
                </>
              )}
            </button>
          </div>

          {/* Prompt update error */}
          {promptUpdateError && (
            <div style={{
              padding: '0 12px 6px',
              fontSize: 11,
              color: STATIC.stopRed,
              fontFamily: '"JetBrains Mono", "Fira Mono", monospace',
            }}>
              {promptUpdateError}
            </div>
          )}

          {/* Divider */}
          <div style={{ height: 1, background: 'rgba(255,255,255,0.07)', margin: '0 0 8px' }} />

          {/* Section 2: Results narrative (or raw detections as fallback) */}
          <div style={{ padding: '0 12px', position: 'relative' }}>
            {/* FPS badge */}
            {metrics.fps > 0 && (
              <span style={{
                position: 'absolute',
                top: 0,
                right: 12,
                fontFamily: '"JetBrains Mono", "Fira Mono", monospace',
                fontSize: 10,
                color: c.textMuted,
                background: 'rgba(255,255,255,0.06)',
                borderRadius: 4,
                padding: '1px 5px',
              }}>
                {samEnabled ? `${metrics.fps} fps` : `Inference: ${metrics.fps} fps`}
              </span>
            )}

            {hasNarrative ? (
              // Show human-readable narrative entries
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {latestNarrative.map((entry, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                    <span style={{ color: STATIC.detectGreen, fontSize: 10, marginTop: 3, flexShrink: 0 }}>●</span>
                    <span style={{
                      fontSize: 13,
                      color: c.textPrimary,
                      lineHeight: '1.4',
                      fontFamily: 'inherit',
                      minHeight: 'calc(13px * 1.4 * 2)', // reserve 2 lines
                    }}>
                      {entry.text}
                    </span>
                  </div>
                ))}
              </div>
            ) : detections.length === 0 ? (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                height: 28,
              }}>
                <span style={{ color: c.textMuted, fontSize: 12 }}>○</span>
                <span style={{
                  fontFamily: '"JetBrains Mono", "Fira Mono", monospace',
                  fontSize: 12,
                  color: c.textMuted,
                }}>
                  Waiting for results...
                </span>
              </div>
            ) : (
              // Fallback: raw detections before narrative arrives
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {detections.map((d, i) => (
                  <div key={i} style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    height: 24,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ color: STATIC.detectGreen, fontSize: 10 }}>●</span>
                      <span style={{
                        fontFamily: '"JetBrains Mono", "Fira Mono", monospace',
                        fontSize: 12,
                        color: c.textPrimary,
                        textTransform: 'capitalize',
                      }}>
                        {d.label}
                      </span>
                    </div>
                    <span style={{
                      fontFamily: '"JetBrains Mono", "Fira Mono", monospace',
                      fontSize: 12,
                      color: c.textMuted,
                    }}>
                      {Math.round(d.confidence * 100)}%
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Bottom safe-area spacer (padding already handled via paddingBottom on parent) */}
        </div>
      </div>
    </>
  );
}
